import { computed, inject, Injectable, signal } from '@angular/core';
import { Router, UrlSerializer } from '@angular/router';
import { CookieService } from 'ngx-unificator/services';
import { Location } from '@angular/common';
import { LocalstorageService } from './localstorage.service';
import { SsApiService } from './api/ss-api.service';
import { WindowService } from './window.service';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

export const PTAG_KEY = 'ptag';

export const COOKIE_ID_ID = 'id_id';
export const HEADER_ID_ID = 'id-id';

export const STAG_URL_KEY = 'stag_url';
export const STAG_KEY = 'stag';

export const PARAMS_TYPE = {
  stag: 'stag',
  ref_code: 'ref_code',
} as const;

@Injectable({
  providedIn: 'root',
})
export class StagDetectorService {

  private _cookie: CookieService = inject(CookieService);
  private _router: Router = inject(Router);
  private _serializer: UrlSerializer = inject(UrlSerializer);
  private _location: Location = inject(Location);
  private _localStorage: LocalstorageService = inject(LocalstorageService);
  private _ssApi: SsApiService = inject(SsApiService);
  private _window: WindowService = inject(WindowService);

  private _state = signal({
    params: this._serializer.parse(this._window.nativeWindow.location.search).queryParams,
    domain: this._window.nativeWindow.location.hostname
      .split('.').slice(-2).join('.'),
  });

  public params = computed(() => this._state().params);
  public domain = computed(() => this._state().domain);

  /**
   * Detect params from url
   */
  public detectParams() {
    this.resolveStagParam$().pipe(
      switchMap(() => {
        const params = this.params();
        if (params?.test) {
          this._resolveTestParam();
        }
        if (params?.nocache) {
          this._resolveNocacheParam();
        }
        if (params?.game) {
          this._resolveGameParam();
        }
        return EMPTY;
      }),
    ).subscribe();
  }

  public resolveStagParam$(): Observable<any> {
    return forkJoin([
      this._resolveParam$(
        PARAMS_TYPE.stag,
        (value) => this._ssApi.infoLocales({ stag: value }),
        (value) => this._setStagData(value),
        () => this._ssApi.infoAffiliate().pipe(map(({ stag }) => stag?.split('_')[0])),
      ),
      this._resolveParam$(
        PARAMS_TYPE.ref_code,
        (value) => this._ssApi.infoLocales({ ref_code: value }),
        (value) => this._setRefCodeData(value),
        () => of(null),
      ),
    ]).pipe(
      map(() => null),
    );
  }


  private _resolveParam$(
    key: keyof typeof PARAMS_TYPE,
    localesApiCall: (value: string) => Observable<any>,
    setter: (value: string) => void,
    fallback$: () => Observable<string | null>,
  ): Observable<string | null> {
    const rawValue = this.params()?.[key];
    const extractedValue = key === PARAMS_TYPE.stag ? rawValue?.split('_')[0] : rawValue;

    const fromParams$ = extractedValue
      ? localesApiCall(rawValue).pipe(
        map(() => extractedValue),
      )
      : null;

    return fromParams$
      ? fromParams$.pipe(tap(setter))
      : fallback$().pipe(tap((value) => value && setter(value)));
  }

  /**
   * Resolve test param
   * @private
   */
  private _resolveTestParam() {
    this._localStorage.set('test', 'true');
    this._updateUrlWithoutParameters();
  }

  /**
   * Resolve nocache param
   * @private
   */
  private _resolveNocacheParam() {
    this._updateUrlWithoutParameters();
  }

  /**
   * Resolve game param
   * @private
   */
  private _resolveGameParam() {
    this._cookie.set('game', this.params()?.game, 999, '/', this.domain());
    this._updateUrlWithoutParameters();
  }

  /**
   * Update url tree
   * @private
   */
  private async _updateUrlWithoutParameters() {
    const tree = this._router.parseUrl(this._location.normalize(window.location.pathname));
    const queryParams = tree.queryParams;
    delete queryParams.param;
    tree.queryParams = queryParams;
    await this._router.navigateByUrl(tree);
    this._state.update(state => {
      return {
        ...state,
        params: {},
      };
    });
  }

  private async _setRefCodeData(value: string) {
    this._localStorage.set(PARAMS_TYPE.ref_code, value);
    await this._updateUrlWithoutParameters();
  }

  /**
   * Storage cookie and local stag data
   * @param value
   * @param domain
   * @private
   */
  private async _setStagData(value: string) {
    value ?
      this._cookie.set(COOKIE_ID_ID, value, 99, '/', this.domain()) :
      this._cookie.delete(COOKIE_ID_ID, '/', this.domain());

    value ?
      this._cookie.set(HEADER_ID_ID, value, 99, '/', this.domain()) :
      this._cookie.delete(HEADER_ID_ID, '/', this.domain());

    value ?
      this._cookie.set(PTAG_KEY, value, 99, '/', this.domain()) :
      this._cookie.delete(PTAG_KEY, '/', this.domain());

    if (!value) {
      this._cookie.delete(STAG_KEY, '/', this.domain());
    }

    value ?
      this._localStorage.set(STAG_URL_KEY, window.location.pathname) :
      this._localStorage.clearItem(STAG_URL_KEY);

    value ?
      this._localStorage.set(STAG_KEY, value) :
      this._localStorage.clearItem(STAG_KEY);

    await this._updateUrlWithoutParameters();
  }
}
